import { useEffect, useState } from "react"
import { Link } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import { ContactEntryBody } from "@/components/v2/drawer/entries/contact-entry-body"
import { DealEntryBody } from "@/components/v2/drawer/entries/deal-entry-body"
import { CompanyEntryBody } from "@/components/v2/drawer/entries/company-entry-body"
import type {
  ContactDrawer as ContactDrawerData,
  DealDrawer as DealDrawerData,
  DrawerActivity,
} from "@/types/v2/conversation"
import type { CompanyDrawer as CompanyDrawerData } from "@/types/v2/company"
import type { DrawerNavContext } from "@/lib/drawer-trail"
import { setPreviewUpdater } from "@/lib/preview-prop-registry"

// The command-palette desktop right pane (CON-183). Renders
// the EXISTING unified-slideover entry bodies (Deal / Contact
// / Company) — fed by the `/v2/preview` JSON endpoint —
// WITHOUT touching the URL-driven `?drawer=` slideover. Every
// edit / navigation inside the body routes through the
// "preview" propKey + `navContext.preview` controls, so the
// host page's URL and Inertia props are never mutated.
//
// Desktop-only: the palette mounts this pane only on desktop,
// so there's no mobile variant.

// Shared with the palette (which imports it to type its
// selection → preview hand-off). Keep this export stable.
export interface PreviewTarget {
  type: "contact" | "deal" | "company"
  id: number
}

// The `/v2/preview` response (camelCase via prop_transformer
// convention, though this is a plain JSON endpoint — the
// backend serializes camelCase to match). `data` is the
// matching entry body's payload; `activity` is the eager
// `DrawerActivity` for contacts only (null otherwise).
interface PreviewResponse {
  type: PreviewTarget["type"]
  id: number
  label: string
  fullPageHref: string
  data: ContactDrawerData | DealDrawerData | CompanyDrawerData
  activity: DrawerActivity | null
}

// Contact activity scope being previewed. Mirrors the URL
// params the slideover uses (`activity_scope` /
// `activity_deal_id`) but lives in pane state here.
interface ScopeParams {
  kind: string
  dealId: number | null
}

export function DrawerPreviewPane({
  target,
}: {
  target: PreviewTarget | null
}) {
  // The pane owns the *effective* target so nested-row clicks
  // (a company's deal, a deal's contact) can swap it without
  // touching the URL. Seeded from the incoming `target` and
  // re-seeded whenever the palette changes it.
  const [activeTarget, setActiveTarget] = useState<PreviewTarget | null>(
    target,
  )
  const [activitySearch, setActivitySearch] = useState("")
  const [scope, setScope] = useState<ScopeParams | null>(null)

  const [data, setData] = useState<PreviewResponse | null>(null)
  const [status, setStatus] = useState<"idle" | "loading" | "error">(
    "idle",
  )

  // When the palette hands us a new target, reset the pane's
  // derived state (activity search + scope belong to the
  // previous entity) and adopt the new target.
  useEffect(() => {
    setActiveTarget(target)
    setActivitySearch("")
    setScope(null)
  }, [target])

  // Register the optimistic updater so inline edits inside the
  // entry body (which dispatch via `replaceDealProp("preview")`
  // / `replaceContactProp("preview")`) patch THIS pane's local
  // data. Cleared on unmount so a stale updater can't outlive
  // the pane.
  useEffect(() => {
    setPreviewUpdater((t) =>
      setData((prev) =>
        prev ? { ...prev, data: t(prev.data) as PreviewResponse["data"] } : prev,
      ),
    )
    return () => setPreviewUpdater(null)
  }, [])

  // Fetch (and re-fetch) the preview whenever the effective
  // target, activity search, or scope changes. Abort the
  // previous request so a fast sequence of target swaps can't
  // land out of order.
  useEffect(() => {
    if (!activeTarget) {
      setData(null)
      setStatus("idle")
      return
    }

    const controller = new AbortController()
    setStatus("loading")

    const params = new URLSearchParams()
    params.set("target", `${activeTarget.type}:${activeTarget.id}`)
    if (activitySearch) params.set("activity_search", activitySearch)
    if (scope) {
      params.set("activity_scope", scope.kind)
      if (scope.dealId != null) {
        params.set("activity_deal_id", String(scope.dealId))
      }
    }

    fetch(`/v2/preview?${params.toString()}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<PreviewResponse>
      })
      .then((body) => {
        setData(body)
        setStatus("idle")
      })
      .catch((err) => {
        // Aborts are expected on rapid target changes — ignore.
        // Match on the name, not the type: some environments /
        // fetch polyfills surface aborts as a plain Error rather
        // than a DOMException, which would otherwise wrongly flip
        // the pane into its error state.
        if (err instanceof Error && err.name === "AbortError") {
          return
        }
        setStatus("error")
      })

    return () => controller.abort()
  }, [activeTarget, activitySearch, scope])

  if (!target) return null

  // Preview-mode nav context. The body talks to the pane via
  // these instead of the URL. `path` is only consulted by the
  // non-preview code paths, which we never hit here.
  const navContext: DrawerNavContext = {
    path:
      typeof window !== "undefined" ? window.location.pathname : "/",
    preserveParams: [],
    reloadProps: [],
    preview: {
      // Calls are never command-palette preview targets, so a
      // `call` drawer type can't reach this pane — narrow it
      // out to keep the preview target's union tight.
      navigate: (type, id) => {
        if (type === "call") return
        setActiveTarget({ type, id })
      },
      searchActivity: (value) => setActivitySearch(value),
      setScope: (next) => setScope(next),
    },
  }

  return (
    <div
      data-testid="drawer-preview-pane"
      className="flex flex-col h-full min-h-0 bg-card"
    >
      {status === "error" ? (
        <PreviewError />
      ) : !data ? (
        <PreviewLoading />
      ) : (
        <>
          <PreviewHeader
            type={data.type}
            label={data.label}
            fullPageHref={data.fullPageHref}
          />
          <div className="flex-1 min-h-0 flex flex-col">
            <PreviewBody data={data} navContext={navContext} />
          </div>
        </>
      )}
    </div>
  )
}

function PreviewBody({
  data,
  navContext,
}: {
  data: PreviewResponse
  navContext: DrawerNavContext
}) {
  switch (data.type) {
    case "deal":
      return (
        <DealEntryBody
          deal={data.data as DealDrawerData}
          navContext={navContext}
          // Owner options aren't shipped to the palette, so the
          // owner inline-edit picker simply offers none. Full
          // owner editing lives on the deal's own page/slideover.
          ownerOptions={[]}
        />
      )
    case "contact":
      return (
        <ContactEntryBody
          contact={data.data as ContactDrawerData}
          navContext={navContext}
          activity={data.activity ?? undefined}
        />
      )
    case "company":
      return (
        <CompanyEntryBody
          company={data.data as CompanyDrawerData}
          navContext={navContext}
        />
      )
  }
}

function PreviewHeader({
  type,
  label,
  fullPageHref,
}: {
  type: PreviewTarget["type"]
  label: string
  fullPageHref: string
}) {
  const typeLabel = useTypeLabel(type)
  return (
    <div className="flex items-center gap-3 px-6 py-3.5 border-b border-[#F0EDE6]">
      <div className="grow min-w-0 flex items-center gap-1.5 text-[13px] leading-4">
        <span className="shrink-0 text-[#9A938A]">{typeLabel}</span>
        <span className="shrink-0 text-[#C9C2B6]">›</span>
        <span className="min-w-0 truncate font-medium text-[#1E1B17]">
          {label}
        </span>
      </div>
      <Link
        href={fullPageHref}
        data-testid="drawer-preview-open-full-page"
        className="shrink-0 flex items-center gap-1 text-[12px] leading-4 font-medium text-[#7A746B] hover:text-[#1E1B17]"
      >
        <Trans>Open full page</Trans>
        <OpenIcon />
      </Link>
    </div>
  )
}

function PreviewLoading() {
  return (
    <div
      data-testid="drawer-preview-loading"
      className="flex-1 flex items-center justify-center text-[#7A746B] text-sm"
    >
      <span className="inline-flex items-center gap-2">
        <Spinner />
        <Trans>Loading preview…</Trans>
      </span>
    </div>
  )
}

function PreviewError() {
  return (
    <div
      data-testid="drawer-preview-error"
      className="flex-1 flex items-center justify-center px-6 text-center"
    >
      <p className="text-[#7A746B] text-[13px] leading-5">
        <Trans>Couldn&apos;t load this preview. Try selecting it again.</Trans>
      </p>
    </div>
  )
}

function useTypeLabel(type: PreviewTarget["type"]): string {
  const { t } = useLingui()
  switch (type) {
    case "contact":
      return t`Contact`
    case "deal":
      return t`Deal`
    case "company":
      return t`Company`
  }
}

function Spinner() {
  return (
    <svg
      className="animate-spin"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="#E5DFD3"
        strokeWidth="2"
      />
      <path
        d="M14 8A6 6 0 0 0 8 2"
        stroke="#1E1B17"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function OpenIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 3H3.5C2.67 3 2 3.67 2 4.5V12.5C2 13.33 2.67 14 3.5 14H11.5C12.33 14 13 13.33 13 12.5V10M9.5 2.5H13.5M13.5 2.5V6.5M13.5 2.5L7 9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
